import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Ban,
  CloudUpload,
  Eye,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ScreenShare,
  ShieldCheck,
  Trash2,
  UsersRound,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, jsonBody } from './api'
import { useAuth } from './auth'
import type { Page } from './types'
import { EmptyState, PageHeader, StatusPill, TableShell } from './ui'

interface Device {
  id: string
  name: string
  status: string
  installationId: string
  model: string | null
  manufacturer: string | null
  androidVersion: string | null
  appVersion: string
  versionCode: number
  orientation: string
  lastSeenAt: string | null
  disabledAt: string | null
  groupId: string | null
  currentPublicationId: string | null
  previousPublicationId: string | null
  createdAt: string
  updatedAt: string
}

interface DeviceDetails extends Device {
  group: { id: string; name: string } | null
  currentPublication: { id: string; name: string; version: number } | null
  previousPublication: { id: string; name: string; version: number } | null
  latestTelemetry: {
    recordedAt: string
    freeStorageBytes: string
    totalStorageBytes: string | null
    uptimeSeconds: string
    currentManifestVersion: number | null
    errors: unknown
  } | null
}

interface Group {
  id: string
  name: string
  description: string | null
  _count?: { devices: number }
}

interface Playlist {
  id: string
  name: string
}

interface Publication {
  id: string
  version: number
  fallbackPlaylistId: string
  targeted: boolean
}

const commands = [
  ['SYNC_NOW', 'Sync now'],
  ['CAPTURE_SCREENSHOT', 'Capture screenshot'],
  ['RESTART_PLAYER', 'Restart player'],
  ['CLEAR_CACHE', 'Clear cache'],
  ['CHECK_FOR_UPDATE', 'Check for update'],
] as const

function formatBytes(value: string | null | undefined) {
  if (!value) return 'Not reported'
  const bytes = Number(value)
  if (!Number.isFinite(bytes)) return 'Not reported'
  const gigabytes = bytes / 1024 ** 3
  return `${gigabytes.toFixed(gigabytes >= 10 ? 1 : 2)} GB`
}

function formatUptime(value: string | null | undefined) {
  if (!value) return 'Not reported'
  const seconds = Number(value)
  if (!Number.isFinite(seconds)) return 'Not reported'
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3_600)
  const minutes = Math.floor((seconds % 3_600) / 60)
  return [days ? `${days}d` : '', hours ? `${hours}h` : '', `${minutes}m`]
    .filter(Boolean)
    .join(' ')
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-lg border bg-muted/20 p-3'>
      <p className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>
        {label}
      </p>
      <p className='mt-1 text-sm font-medium break-words'>{value}</p>
    </div>
  )
}

export function DevicesPage() {
  const queryClient = useQueryClient()
  const { role } = useAuth()
  const canPublish = role === 'TENANT_OWNER' || role === 'TENANT_ADMIN'
  const canManageDevice =
    role === 'TENANT_OWNER' ||
    role === 'TENANT_ADMIN' ||
    role === 'DEVICE_MANAGER'
  const [open, setOpen] = useState(false)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [removeOpen, setRemoveOpen] = useState(false)
  const [publishSelections, setPublishSelections] = useState<
    Record<string, string>
  >({})
  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api<Page<Device>>('/devices?limit=100'),
    refetchInterval: 30_000,
  })
  const playlists = useQuery({
    queryKey: ['playlists'],
    queryFn: () => api<Playlist[]>('/playlists'),
    enabled: canPublish,
  })
  const publications = useQuery({
    queryKey: ['publications'],
    queryFn: () => api<Publication[]>('/publications'),
    enabled: canPublish,
  })
  const groups = useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api<Group[]>('/device-groups'),
  })
  const deviceDetails = useQuery({
    queryKey: ['device', selectedDeviceId],
    queryFn: () => api<DeviceDetails>(`/devices/${selectedDeviceId}`),
    enabled: Boolean(selectedDeviceId),
  })
  const pair = useMutation({
    mutationFn: (input: unknown) =>
      api('/devices/pairings/claim', { method: 'POST', body: jsonBody(input) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] })
      setOpen(false)
      toast.success(
        'Screen paired. The player can now retrieve its one-time credential.'
      )
    },
    onError: (error) => toast.error(error.message),
  })
  const defaultPublishPlaylistId =
    publications.data?.find((publication) => !publication.targeted)
      ?.fallbackPlaylistId ||
    playlists.data?.[0]?.id ||
    ''
  const publishToDevice = useMutation({
    mutationFn: ({
      deviceId,
      fallbackPlaylistId,
    }: {
      deviceId: string
      fallbackPlaylistId: string
    }) =>
      api<{ version: number }>('/publications/publish', {
        method: 'POST',
        body: jsonBody({ fallbackPlaylistId, deviceIds: [deviceId] }),
      }),
    onSuccess: async (publication, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['publications'] }),
        queryClient.invalidateQueries({ queryKey: ['devices'] }),
      ])
      const deviceName = devices.data?.items.find(
        (device) => device.id === variables.deviceId
      )?.name
      toast.success(
        `Version ${publication.version} published to ${deviceName ?? 'screen'}.`
      )
    },
    onError: (error) => toast.error(error.message),
  })
  const command = useMutation({
    mutationFn: ({ deviceId, type }: { deviceId: string; type: string }) =>
      api(`/devices/${deviceId}/commands`, {
        method: 'POST',
        body: jsonBody({ type }),
      }),
    onSuccess: () =>
      toast.success('Command queued and signaled to the player.'),
    onError: (error) => toast.error(error.message),
  })
  const setBlocked = useMutation({
    mutationFn: ({
      deviceId,
      disabled,
    }: {
      deviceId: string
      disabled: boolean
    }) =>
      api<Device>(`/devices/${deviceId}`, {
        method: 'PATCH',
        body: jsonBody({ disabled }),
      }),
    onSuccess: async (device) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['devices'] }),
        queryClient.invalidateQueries({ queryKey: ['device', device.id] }),
      ])
      toast.success(
        device.disabledAt
          ? 'Device blocked. Its credential can no longer access content.'
          : 'Device unblocked.'
      )
    },
    onError: (error) => toast.error(error.message),
  })
  const removeDevice = useMutation({
    mutationFn: (deviceId: string) =>
      api<void>(`/devices/${deviceId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] })
      setRemoveOpen(false)
      setSelectedDeviceId(null)
      toast.success('Device removed and its credentials revoked.')
    },
    onError: (error) => toast.error(error.message),
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    pair.mutate({
      code: String(data.get('code') ?? '')
        .replace(/\s/g, '')
        .toUpperCase(),
      name: data.get('name'),
      groupId: data.get('groupId') || null,
    })
  }
  const selectedDevice = deviceDetails.data
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Fleet'
        title='Screen network'
        description='Enroll players inside quota, watch heartbeat health, organize groups, and issue only supported user-safe commands.'
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <ScreenShare className='size-4' />
                Pair a screen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Pair an Android TV</DialogTitle>
                <DialogDescription>
                  Enter the eight-character code shown by the player. Codes
                  expire after 15 minutes and can be used once.
                </DialogDescription>
              </DialogHeader>
              <form className='space-y-4' onSubmit={submit}>
                <div className='space-y-2'>
                  <Label htmlFor='pair-code'>Pairing code</Label>
                  <Input
                    id='pair-code'
                    name='code'
                    className='h-14 text-center font-mono text-2xl font-black tracking-[.3em] uppercase'
                    minLength={8}
                    maxLength={8}
                    pattern='[A-Za-z2-9]{8}'
                    required
                    autoFocus
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='device-name'>Screen name</Label>
                  <Input
                    id='device-name'
                    name='name'
                    placeholder='Reception screen'
                    required
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='device-group'>
                    Group{' '}
                    <span className='font-normal text-muted-foreground'>
                      (optional)
                    </span>
                  </Label>
                  <select
                    id='device-group'
                    name='groupId'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  >
                    <option value=''>No group</option>
                    {groups.data?.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <Button className='w-full' disabled={pair.isPending}>
                  {pair.isPending ? 'Claiming…' : 'Claim screen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <Dialog
        open={Boolean(selectedDeviceId)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSelectedDeviceId(null)
        }}
      >
        <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-3xl'>
          <DialogHeader>
            <DialogTitle className='flex flex-wrap items-center gap-3'>
              {selectedDevice?.name ?? 'Device details'}
              {selectedDevice ? (
                <StatusPill value={selectedDevice.status} />
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Complete player identity, health, content, and enrollment details.
            </DialogDescription>
          </DialogHeader>
          {deviceDetails.isLoading ? (
            <p className='rounded-lg border p-6 text-sm text-muted-foreground'>
              Loading device details…
            </p>
          ) : deviceDetails.isError ? (
            <p className='rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
              {deviceDetails.error.message}
            </p>
          ) : selectedDevice ? (
            <div className='space-y-5'>
              <section>
                <h3 className='mb-2 text-sm font-semibold'>Player identity</h3>
                <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
                  <DetailItem
                    label='Model'
                    value={selectedDevice.model ?? 'Android TV'}
                  />
                  <DetailItem
                    label='Manufacturer'
                    value={selectedDevice.manufacturer ?? 'Not reported'}
                  />
                  <DetailItem
                    label='Android'
                    value={selectedDevice.androidVersion ?? 'Not reported'}
                  />
                  <DetailItem
                    label='Player version'
                    value={`${selectedDevice.appVersion} (${selectedDevice.versionCode})`}
                  />
                  <DetailItem
                    label='Orientation'
                    value={selectedDevice.orientation.toLowerCase()}
                  />
                  <DetailItem
                    label='Group'
                    value={selectedDevice.group?.name ?? 'No group'}
                  />
                </div>
              </section>

              <section>
                <h3 className='mb-2 text-sm font-semibold'>Content state</h3>
                <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
                  <DetailItem
                    label='Current publication'
                    value={
                      selectedDevice.currentPublication
                        ? `${selectedDevice.currentPublication.name} · v${selectedDevice.currentPublication.version}`
                        : 'None'
                    }
                  />
                  <DetailItem
                    label='Previous publication'
                    value={
                      selectedDevice.previousPublication
                        ? `${selectedDevice.previousPublication.name} · v${selectedDevice.previousPublication.version}`
                        : 'None'
                    }
                  />
                  <DetailItem
                    label='Manifest version'
                    value={
                      selectedDevice.latestTelemetry?.currentManifestVersion?.toString() ??
                      'Not reported'
                    }
                  />
                </div>
              </section>

              <section>
                <h3 className='mb-2 text-sm font-semibold'>Health & storage</h3>
                <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
                  <DetailItem
                    label='Last seen'
                    value={
                      selectedDevice.lastSeenAt
                        ? new Date(selectedDevice.lastSeenAt).toLocaleString()
                        : 'Never'
                    }
                  />
                  <DetailItem
                    label='Free storage'
                    value={formatBytes(
                      selectedDevice.latestTelemetry?.freeStorageBytes
                    )}
                  />
                  <DetailItem
                    label='Total storage'
                    value={formatBytes(
                      selectedDevice.latestTelemetry?.totalStorageBytes
                    )}
                  />
                  <DetailItem
                    label='Player uptime'
                    value={formatUptime(
                      selectedDevice.latestTelemetry?.uptimeSeconds
                    )}
                  />
                  <DetailItem
                    label='Telemetry received'
                    value={
                      selectedDevice.latestTelemetry?.recordedAt
                        ? new Date(
                            selectedDevice.latestTelemetry.recordedAt
                          ).toLocaleString()
                        : 'Never'
                    }
                  />
                  <DetailItem
                    label='Errors'
                    value={
                      Array.isArray(selectedDevice.latestTelemetry?.errors) &&
                      selectedDevice.latestTelemetry.errors.length
                        ? selectedDevice.latestTelemetry.errors.join(', ')
                        : 'None reported'
                    }
                  />
                </div>
              </section>

              <section>
                <h3 className='mb-2 text-sm font-semibold'>Enrollment</h3>
                <div className='grid gap-2 sm:grid-cols-2'>
                  <DetailItem
                    label='Installation ID'
                    value={selectedDevice.installationId}
                  />
                  <DetailItem label='Device ID' value={selectedDevice.id} />
                  <DetailItem
                    label='Enrolled'
                    value={new Date(selectedDevice.createdAt).toLocaleString()}
                  />
                  <DetailItem
                    label='Last updated'
                    value={new Date(selectedDevice.updatedAt).toLocaleString()}
                  />
                </div>
              </section>

              {canManageDevice ? (
                <section className='flex flex-col gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between'>
                  <div>
                    <p className='text-sm font-semibold'>Device access</p>
                    <p className='mt-1 text-xs text-muted-foreground'>
                      Blocking is reversible. Removing revokes credentials and
                      hides this device while retaining audit and play history.
                    </p>
                  </div>
                  <div className='flex shrink-0 flex-wrap gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      disabled={setBlocked.isPending}
                      onClick={() =>
                        setBlocked.mutate({
                          deviceId: selectedDevice.id,
                          disabled: !selectedDevice.disabledAt,
                        })
                      }
                    >
                      {selectedDevice.disabledAt ? (
                        <ShieldCheck className='size-4' />
                      ) : (
                        <Ban className='size-4' />
                      )}
                      {selectedDevice.disabledAt
                        ? 'Unblock device'
                        : 'Block device'}
                    </Button>
                    <Button
                      type='button'
                      variant='destructive'
                      onClick={() => setRemoveOpen(true)}
                    >
                      <Trash2 className='size-4' />
                      Remove device
                    </Button>
                  </div>
                </section>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this device?</AlertDialogTitle>
            <AlertDialogDescription>
              The player credential will be revoked and the device will be
              hidden from the fleet. Audit and proof-of-play history will be
              retained. Reconnecting requires a fresh player pairing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className='text-destructive-foreground bg-destructive hover:bg-destructive/90'
              disabled={removeDevice.isPending}
              onClick={() => {
                if (selectedDeviceId) removeDevice.mutate(selectedDeviceId)
              }}
            >
              {removeDevice.isPending ? 'Removing…' : 'Remove device'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {devices.data?.items.length ? (
        <TableShell
          headers={[
            'Screen',
            'State',
            'Model',
            'Player',
            'Last seen',
            'Content',
            '',
          ]}
        >
          {devices.data.items.map((device) => (
            <tr key={device.id}>
              <td className='px-4 py-3'>
                <p className='font-semibold'>{device.name}</p>
                <p className='mt-1 font-mono text-[10px] text-muted-foreground'>
                  {device.installationId.slice(0, 8)}
                </p>
              </td>
              <td className='px-4 py-3'>
                <StatusPill value={device.status} />
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {device.model ?? 'Android TV'}
              </td>
              <td className='px-4 py-3 font-mono text-xs'>
                {device.appVersion}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {device.lastSeenAt
                  ? new Date(device.lastSeenAt).toLocaleString()
                  : 'Never'}
              </td>
              <td className='min-w-64 px-4 py-3'>
                {canPublish ? (
                  <div className='flex items-center gap-2'>
                    <select
                      aria-label={`Playlist for ${device.name}`}
                      className='h-8 min-w-36 rounded-md border bg-background px-2 text-xs'
                      value={
                        publishSelections[device.id] ?? defaultPublishPlaylistId
                      }
                      onChange={(event) =>
                        setPublishSelections((current) => ({
                          ...current,
                          [device.id]: event.target.value,
                        }))
                      }
                    >
                      <option value=''>Select playlist</option>
                      {playlists.data?.map((playlist) => (
                        <option key={playlist.id} value={playlist.id}>
                          {playlist.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      type='button'
                      size='sm'
                      variant='outline'
                      disabled={
                        !(
                          publishSelections[device.id] ??
                          defaultPublishPlaylistId
                        ) || publishToDevice.isPending
                      }
                      onClick={() =>
                        publishToDevice.mutate({
                          deviceId: device.id,
                          fallbackPlaylistId:
                            publishSelections[device.id] ??
                            defaultPublishPlaylistId,
                        })
                      }
                    >
                      <CloudUpload className='size-3.5' />
                      Publish
                    </Button>
                  </div>
                ) : (
                  <span className='text-xs text-muted-foreground'>
                    Admin publishing only
                  </span>
                )}
              </td>
              <td className='px-4 py-3 text-end'>
                <div className='flex items-center justify-end gap-1'>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => setSelectedDeviceId(device.id)}
                    aria-label={`View details for ${device.name}`}
                  >
                    <Eye className='size-4' />
                    View
                  </Button>
                  {canManageDevice ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='icon'
                          variant='ghost'
                          aria-label={`Commands for ${device.name}`}
                        >
                          <MoreHorizontal className='size-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        {commands.map(([type, label]) => (
                          <DropdownMenuItem
                            key={type}
                            onSelect={() =>
                              command.mutate({ deviceId: device.id, type })
                            }
                          >
                            {type === 'SYNC_NOW' && (
                              <RefreshCw className='size-4' />
                            )}
                            {label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      ) : (
        <EmptyState
          title='No screens enrolled'
          description='Install the DOT IT Signage player, open it on a TV, and enter its temporary pairing code.'
          action={
            <Button onClick={() => setOpen(true)}>Pair first screen</Button>
          }
        />
      )}
    </div>
  )
}

export function GroupsPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const groups = useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api<Group[]>('/device-groups'),
  })
  const create = useMutation({
    mutationFn: (input: unknown) =>
      api('/device-groups', { method: 'POST', body: jsonBody(input) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['device-groups'] })
      setOpen(false)
      toast.success('Device group created.')
    },
    onError: (error) => toast.error(error.message),
  })
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    create.mutate({
      name: data.get('name'),
      description: data.get('description') || undefined,
    })
  }
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Fleet'
        title='Device groups'
        description='Organize screens by site, department, or campaign target. Schedules can target groups without copying playlists.'
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className='size-4' />
                New group
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create a group</DialogTitle>
                <DialogDescription>
                  Choose a clear operational name such as “Dhaka branches”.
                </DialogDescription>
              </DialogHeader>
              <form className='space-y-4' onSubmit={submit}>
                <div className='space-y-2'>
                  <Label htmlFor='group-name'>Name</Label>
                  <Input id='group-name' name='name' required />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='group-description'>Description</Label>
                  <Input id='group-description' name='description' />
                </div>
                <Button className='w-full' disabled={create.isPending}>
                  {create.isPending ? 'Creating…' : 'Create group'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      {groups.data?.length ? (
        <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-3'>
          {groups.data.map((group) => (
            <article key={group.id} className='rounded-xl border bg-card p-5'>
              <span className='grid size-10 place-items-center rounded-xl bg-primary/10 text-primary'>
                <UsersRound className='size-5' />
              </span>
              <h2 className='mt-4 font-semibold'>{group.name}</h2>
              <p className='mt-2 min-h-10 text-sm text-muted-foreground'>
                {group.description || 'No description'}
              </p>
              <p className='mt-4 text-xs font-medium'>
                {group._count?.devices ?? 0} screens
              </p>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          title='No device groups'
          description='Create a reusable target before scheduling across multiple screens.'
          action={<Button onClick={() => setOpen(true)}>Create group</Button>}
        />
      )}
    </div>
  )
}
